<template>
  <div class="blog-header">
    <div class="hero-image-wrapper" v-if="props.image">
      <NuxtImg
        v-if="cdnUrl + props.image"
        :src="cdnUrl + props.image"
        :alt="props.title"
        width="346"
        height="346"
        class="hero-image"
        preload
        fetchPriority="high"
      />
    </div>
    <div class="header-content">
      <div class="categories" v-if="props.categories.length">
        <span
          v-for="category in props.categories"
          :key="category"
          class="category-tag"
        >
          {{ category.toUpperCase() }}
        </span>
      </div>
      <h1 class="title">{{ props.title }}</h1>
      <p class="excerpt" v-if="props.excerpt">{{ props.excerpt }}</p>
      <div class="metadata">
        <span class="author"
          >Posted by
          <NuxtLink :to="baseUrl" external>{{ props.author }}</NuxtLink></span
        >
        <span class="separator">•</span>
        <span class="date">{{ formattedDate }}</span>
        <span v-if="showUpdated" class="separator">•</span>
        <span v-if="showUpdated" class="updated"
          >Updated on {{ formattedUpdatedDate }}</span
        >
        <span class="separator">•</span>
        <span class="read-time">{{ props.readTime }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
const config = useRuntimeConfig();
const cdnUrl = config.public.cdnUrl;
const baseUrl = config.public.baseUrl;
const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  excerpt: {
    type: String,
    default: "",
  },
  image: {
    type: String,
    required: true,
  },
  author: {
    type: String,
    default: "Travvir team",
  },
  date: {
    type: String,
    required: true,
  },
  updatedAt: {
    type: String,
    default: "",
  },
  categories: {
    type: Array,
    default: () => [],
  },
  readTime: {
    type: String,
    default: "5 min read",
  },
});

const formattedDate = computed(() => {
  const date = new Date(props.date);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
});

const showUpdated = computed(() => {
  return Boolean(props.updatedAt) && props.updatedAt !== props.date;
});

const formattedUpdatedDate = computed(() => {
  const date = new Date(props.updatedAt);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
});
</script>

<style lang="scss" scoped>
.blog-header {
  font-family: $theme-font;
  margin-bottom: 3rem;

  @include respond-below(sm) {
    margin-bottom: 2rem;
  }

  .hero-image-wrapper {
    position: relative;
    width: 100%;
    height: 500px;
    margin-bottom: 2.5rem;
    border-radius: 12px;
    overflow: hidden;
    background: #1a1a1a;

    @include respond-below(md) {
      height: 400px;
    }

    @include respond-below(sm) {
      height: 280px;
      border-radius: 8px;
      margin-bottom: 2rem;
    }

    .hero-image {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .header-content {
    .categories {
      display: flex;
      gap: 0.75rem;
      margin-bottom: 1.25rem;
      flex-wrap: wrap;

      .category-tag {
        font-size: 0.7rem;
        font-weight: 600;
        letter-spacing: 0.05em;
        color: rgba(255, 255, 255, 0.6);
        padding: 0.4rem 1rem;
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 4px;
        text-transform: uppercase;

        @include respond-below(sm) {
          font-size: 0.65rem;
          padding: 0.35rem 0.85rem;
        }
      }
    }

    .title {
      font-family: $theme-font;
      font-size: 2.75rem;
      font-weight: 600;
      line-height: 1.2;
      color: rgba(255, 255, 255, 0.95);
      margin-bottom: 1rem;

      @include respond-below(md) {
        font-size: 2.25rem;
      }

      @include respond-below(sm) {
        font-size: 1.75rem;
      }
    }

    .excerpt {
      font-family: $theme-font;
      font-size: 1.125rem;
      line-height: 1.6;
      color: rgba(255, 255, 255, 0.65);
      margin-bottom: 1.5rem;

      @include respond-below(sm) {
        font-size: 1rem;
        margin-bottom: 1.25rem;
      }
    }

    .metadata {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      font-size: 0.875rem;
      color: rgba(255, 255, 255, 0.5);
      flex-wrap: wrap;
      padding-top: 1rem;
      border-top: 1px solid rgba(255, 255, 255, 0.08);

      @include respond-below(sm) {
        font-size: 0.8125rem;
        gap: 0.5rem;
        padding-top: 0.875rem;
      }
      a {
        color: inherit;
        text-decoration: underline;
      }

      .author,
      .date,
      .read-time,
      a {
        font-weight: 400;
      }

      .separator {
        color: rgba(255, 255, 255, 0.25);
      }
    }
  }
}
</style>
